import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import HeroSection from './HeroSection'
import AboutSection from './AboutSection'
import FeaturesSection from './FeaturesSection'
import HowItWorksSection from './HowItWorksSection'
import FooterSection from './FooterSection'
import styles from './LandingPage.module.css'
import logo from '../../assets/logos/logo.png'
import AuthModal from '../Auth/AuthModal'
import { getAccessToken, clearAccessToken } from '../Auth/AuthStore'

const LandingPage = () => { 
  const [currentSection, setCurrentSection] = useState(0)
  const [authOpen, setAuthOpen] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const containerRef = useRef(null)
  const navigate = useNavigate()
  const [vh, setVh] = useState(window.innerHeight);
  const touchStartY = useRef(null)
  const touchStartTime = useRef(null)

  const sections = [
    { id: 'hero', component: HeroSection, background: 'linear-gradient(135deg,#ceb0ff 0%, #a862fd 100%)' },
    { id: 'about', component: AboutSection, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { id: 'features', component: FeaturesSection, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { id: 'how-it-works', component: HowItWorksSection, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
    { id: 'footer', component: FooterSection, background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)' }
  ]

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
  
    if (authOpen) {
      return
    }
  
    container.addEventListener('wheel', handleScroll, { passive: false })
    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: false })
    document.addEventListener('keydown', handleKeyDown)
  
    return () => {
      container.removeEventListener('wheel', handleScroll)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [authOpen, currentSection, isScrolling])
  

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 액세스 토큰이 있으면 유효성 검사 후 대시보드로 자동 이동
  useEffect(() => {
    const checkTokenAndNavigate = async () => {
      const token = getAccessToken()
      if (!token) {
        return
      }

      try {
        // 토큰 유효성 검사
        const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/user/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.status === 200 && response.data?.data) {
          navigate('/dashboard')
        }
      } catch (error) {
        console.error('토큰 유효성 검사 실패:', error)
        clearAccessToken()
      }
    }

    checkTokenAndNavigate()
  }, [navigate])

  // 스크롤 이벤트 핸들러
  const handleScroll = (event) => {
    if (isScrolling) return

    event.preventDefault()
    setIsScrolling(true)

    const delta = event.deltaY
    let nextSection = currentSection

    if (delta > 0 && currentSection < sections.length - 1) {
      nextSection = currentSection + 1
    } else if (delta < 0 && currentSection > 0) {
      nextSection = currentSection - 1
    }

    if (nextSection !== currentSection) {
      setCurrentSection(nextSection)
    }

    setTimeout(() => {
      setIsScrolling(false)
    }, 1000)
  }

  const handleKeyDown = (event) => {
    if (isScrolling) return

    switch (event.key) {
      case 'ArrowDown':
      case 'PageDown':
        event.preventDefault()
        if (currentSection < sections.length - 1) {
          setCurrentSection(currentSection + 1)
        }
        break
      case 'ArrowUp':
      case 'PageUp':
        event.preventDefault()
        if (currentSection > 0) {
          setCurrentSection(currentSection - 1)
        }
        break
      case 'Home':
        event.preventDefault()
        setCurrentSection(0)
        break
      case 'End':
        event.preventDefault()
        setCurrentSection(sections.length - 1)
        break
    }
  }

  // 터치 이벤트 핸들러
  const handleTouchStart = (event) => {
    if (isScrolling) return
    const touch = event.touches[0]
    touchStartY.current = touch.clientY
    touchStartTime.current = Date.now()
  }

  const handleTouchMove = (event) => {
    if (touchStartY.current === null) return
    // 스크롤 방지
    event.preventDefault()
  }

  const handleTouchEnd = (event) => {
    if (touchStartY.current === null || isScrolling) {
      touchStartY.current = null
      touchStartTime.current = null
      return
    }

    const touch = event.changedTouches[0]
    const touchEndY = touch.clientY
    const touchEndTime = Date.now()
    
    const deltaY = touchEndY - touchStartY.current
    const deltaTime = touchEndTime - (touchStartTime.current || touchEndTime)
    
    const minSwipeDistance = 50
    const maxSwipeTime = 500
    
    if (Math.abs(deltaY) >= minSwipeDistance && deltaTime <= maxSwipeTime) {
      event.preventDefault()
      setIsScrolling(true)
      
      let nextSection = currentSection
      

      if (deltaY > 0 && currentSection > 0) {
        nextSection = currentSection - 1
      } 

      else if (deltaY < 0 && currentSection < sections.length - 1) {
        nextSection = currentSection + 1
      }
      
      if (nextSection !== currentSection) {
        setCurrentSection(nextSection)
      }
      
      setTimeout(() => {
        setIsScrolling(false)
      }, 1000)
    }
    
    touchStartY.current = null
    touchStartTime.current = null
  }

  const goSignUp = () => {
    navigate('/signup')
  }

  const goToDrawing = () => {
    navigate('/drawing')
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('wheel', handleScroll, { passive: false })
      container.addEventListener('touchstart', handleTouchStart, { passive: false })
      container.addEventListener('touchmove', handleTouchMove, { passive: false })
      container.addEventListener('touchend', handleTouchEnd, { passive: false })
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        container.removeEventListener('wheel', handleScroll)
        container.removeEventListener('touchstart', handleTouchStart)
        container.removeEventListener('touchmove', handleTouchMove)
        container.removeEventListener('touchend', handleTouchEnd)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [currentSection, isScrolling])

  return (
    <div 
      ref={containerRef}
      className={styles.landingContainer}
      style={{ 
        background: sections[currentSection]?.background,
      }}
    >
      {/* 네비게이션 도트 */}
      <div className={styles.navigation}>
        {sections.map((_, index) => (
          <button
            key={index}
            className={`${styles.navDot} ${
              currentSection === index ? styles.active : ''
            }`}
            onClick={() => setCurrentSection(index)}
            aria-label={`섹션 ${index + 1}로 이동`}
          />
        ))}
      </div>

      {/* 스크롤 힌트 */}
      {currentSection === 0 && (
        <div className={styles.scrollHint}>
          <div className={styles.scrollIndicator}>
            <i className="fas fa-chevron-down"></i>
          </div>
          <span>스크롤하여 더 알아보기</span>
        </div>
      )}

      {/* 섹션 트랙(wrapper) */}
      <div
        className={styles.sectionsWrapper}
        style={{
         height: `${sections.length * vh}px`,
         transform: `translateY(-${currentSection * 100}vh)`,
        }}
      >
        {/* 섹션들 */}
        {sections.map((section, index) => {
          const SectionComponent = section.component
          const extraProps =
          section.id === 'footer'
            ? { onBackToTop: () => setCurrentSection(0) }  
            : {}  

          return (
            <div
              key={section.id}
              className={`${styles.section} ${
                currentSection === index ? styles.active : ''
              }`}
              style={{ background: section.background }}
            >
              <SectionComponent 
                isActive={currentSection === index}
                onStartDrawing={goToDrawing}
                onNextSection={() => {
                  if (index < sections.length - 1) {
                    setCurrentSection(index + 1)
                  }
                }}
                {...extraProps}
              />
            </div>
          )
        })}
      </div>

      {/* 로고 및 상단 네비게이션 */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <img src={logo} alt = "로고" draggable={false}/>
          </div>
          
          <nav className={styles.headerNav}>
            <button onClick={() => setCurrentSection(0)}>홈</button>
            <button onClick={() => setCurrentSection(1)}>소개</button>
            <button onClick={() => setCurrentSection(2)}>기능</button>
            <button onClick={() => setCurrentSection(3)}>이용방법</button>
          </nav>

          <div className={styles.headerActions}>
            <button
              className={styles.loginBtn}
              onClick={() => setAuthOpen(true)}
            >
              로그인
            </button>
            <button className={styles.startBtn} onClick={goSignUp}>
              시작하기
            </button>
          </div>
        </div>
      </header>


            <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMode="signin"
      />
    </div>
  )
}

export default LandingPage